import React, {useEffect, useState} from 'react';
import {Button, useToast} from '@chakra-ui/react';
import {useAgent, useCustomer} from '../../../../hooks/api/metadata';
import {useGetSegmentationAgentPrompt} from '../../../../hooks/api/agents';
import {Parser} from '@json2csv/plainjs';
import FileSaver from 'file-saver';
import {ObjectId} from 'bson';
import {Download} from '@carbon/icons-react';

export const DownloadSegmentsPromptButton = ({
  agentId,
  taskId,
}: {
  agentId: ObjectId;
  taskId: ObjectId;
}) => {
  const customer = useCustomer();
  const toast = useToast();
  const agent = useAgent(agentId);

  const [isDownloadingPrompt, setIsDownloadingPrompt] = useState(false);
  const {
    data: prompt,
    isError: isDownloadingPromptError,
    isFetching: isDownloadingPromptFetching,
    isSuccess: isDownloadingPromptSuccess,
  } = useGetSegmentationAgentPrompt({taskId}, {enabled: isDownloadingPrompt});

  useEffect(() => {
    if (isDownloadingPromptError) {
      toast({
        title: 'Error downloading prompts.',
        description: 'Please try again or contact support.',
        status: 'error',
      });
      setIsDownloadingPrompt(false);
    }
  }, [isDownloadingPromptError, isDownloadingPrompt, toast]);

  useEffect(() => {
    if (
      !isDownloadingPrompt ||
      isDownloadingPromptFetching ||
      !isDownloadingPromptSuccess
    ) {
      return;
    }

    const csv = new Parser().parse(prompt);
    const blob = new Blob([csv], {type: 'text/csv'});
    FileSaver.saveAs(
      blob,
      `Segmentation Agent Prompt-${customer.name}-${agent?.label ?? agentId}`
    );

    setIsDownloadingPrompt(false);
  }, [
    customer.name,
    agent?.label,
    agentId,
    prompt,
    isDownloadingPrompt,
    isDownloadingPromptFetching,
    isDownloadingPromptSuccess,
  ]);

  return (
    <Button
      aria-label="Download segmentation prompts as CSV"
      colorScheme="kbuttonblue"
      isLoading={isDownloadingPrompt}
      onClick={() => setIsDownloadingPrompt(true)}
      opacity={0.5}
      variant="ghost"
    >
      <Download />
    </Button>
  );
};
